// Group picker for Shift+C / Shift+V (§11.5): checkboxes, remembered.
import { useState } from 'react';
import { useClipboard } from '../../state/clipboard';
import { SYNC_GROUPS, type SyncGroup } from '../../model/types';
import { useT, type MessageKey } from '../../i18n';
import s from './Dialog.module.css';

export const GROUP_LABEL: Record<SyncGroup, MessageKey> = {
  light: 'panelLight',
  wb: 'panelWhiteBalance',
  color: 'panelColor',
  hsl: 'panelHsl',
  curve: 'panelCurve',
  detail: 'panelDetail',
  crop: 'groupCrop',
  perspective: 'groupPerspective',
  masks: 'panelMasks',
  heal: 'panelHeal',
  skin: 'panelSkin',
  cleanWhites: 'panelCleanWhites',
  lut: 'groupLut',
  effects: 'panelEffects',
  background: 'panelBackground',
};

export function GroupChecklist({
  value,
  onChange,
}: {
  value: SyncGroup[];
  onChange: (g: SyncGroup[]) => void;
}) {
  const t = useT();
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        <button onClick={() => onChange([...SYNC_GROUPS])}>{t('selectAllGroups')}</button>
        <button onClick={() => onChange([])}>{t('selectNoGroups')}</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
        {SYNC_GROUPS.map((g) => (
          <label key={g} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              data-group={g}
              checked={value.includes(g)}
              onChange={(e) => onChange(e.target.checked ? [...value, g] : value.filter((x) => x !== g))}
            />
            {t(GROUP_LABEL[g])}
          </label>
        ))}
      </div>
    </div>
  );
}

export function SyncDialog() {
  const t = useT();
  const picker = useClipboard((st) => st.picker);
  const remembered = useClipboard((st) => st.groups);
  const [groups, setGroups] = useState<SyncGroup[]>(remembered);
  if (!picker) return null;
  return (
    <div
      className={s.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sync-title"
      onKeyDown={(e) => e.key === 'Escape' && useClipboard.getState().closePicker()}
    >
      <div className={s.dialog} data-testid="sync-dialog">
        <h2 id="sync-title">{t('syncTitle')}</h2>
        <GroupChecklist value={groups} onChange={setGroups} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={() => useClipboard.getState().closePicker()}>{t('cancel')}</button>
          <button
            data-testid="sync-ok"
            style={{ background: 'var(--accent-fill)', color: 'var(--on-accent)' }}
            onClick={() => useClipboard.getState().confirm(groups)}
          >
            {picker === 'paste' ? t('pasteSettings') : t('copySettings')}
          </button>
        </div>
      </div>
    </div>
  );
}
